"use client";

import { useEffect, useMemo, useCallback } from "react";
import useSWRInfinite from "swr/infinite";
import {
  ContestAPI,
  ContestResponse,
  ContestResponseData,
  ContestSearchRequest,
  ContestScreenType,
} from "@/restapi/contest";
import { SortBy, SortOrder } from "@/components/contest/list/ContestSortSection";
import { buildContestListKey } from "@/lib/swr/contest";

export type UseContestSearchParams = {
  enabled?: boolean;
  perPage?: number;
  isPublished?: boolean;
  isLocked?: boolean;
  q?: string;
  groupId?: number;
  userIds?: string[];
  defaultToSessionUserId?: boolean;
  actorUuid?: string;
  statuses?: string[];
  categoryIds?: number[];
  startFrom?: string;
  startTo?: string;
  sortBy?: SortBy;
  sortOrder?: SortOrder;
  viewer?: "admin" | "user";
  screen: ContestScreenType;
};

const normalizeStringArray = (v?: Array<string | null | undefined>) =>
  (v ?? []).map((x) => (x ?? "").trim()).filter((x) => x.length > 0);

const normalizeNumberArray = (v?: Array<number | string | null | undefined>) =>
  (v ?? []).map((x) => Number(x)).filter((n) => Number.isFinite(n)) as number[];

export const useContestSearch = (params: UseContestSearchParams) => {
  const enabled = params.enabled ?? true;

  const stableParams: ContestSearchRequest = useMemo(() => {
    const actor = params.actorUuid?.trim() ? params.actorUuid.trim() : undefined;
    const rawUserIds = normalizeStringArray(params.userIds);

    const normalizedUserIds =
      rawUserIds.length > 0
        ? rawUserIds
        : params.defaultToSessionUserId && actor
          ? [actor]
          : [];

    return {
      screen: params.screen,
      viewer: params.viewer,
      perPage: params.perPage ?? 10,
      isPublished: params.isPublished,
      isLocked: params.isLocked,
      q: params.q?.trim() ? params.q.trim() : undefined,
      groupId: params.groupId,

      statuses: normalizeStringArray(params.statuses),
      categoryIds: normalizeNumberArray(params.categoryIds),
      userIds: normalizedUserIds,

      startFrom: params.startFrom ?? undefined,
      startTo: params.startTo ?? undefined,
      sortBy: params.sortBy,
      sortOrder: params.sortOrder,
      actorUuid: actor,
    };
  }, [
    params.screen,
    params.viewer,
    params.perPage,
    params.isPublished,
    params.isLocked,
    params.q,
    params.groupId,
    params.startFrom,
    params.startTo,
    params.defaultToSessionUserId,
    params.actorUuid,
    params.sortBy,
    params.sortOrder,
    JSON.stringify(params.statuses ?? []),
    JSON.stringify(params.categoryIds ?? []),
    JSON.stringify(params.userIds ?? []),
  ]);

  const paramsKey = useMemo(() => buildContestListKey(stableParams), [stableParams]);

  const getKey = useCallback(
    (index: number, previousPageData: ContestResponse | null) => {
      if (!enabled) return null;
      if (previousPageData && previousPageData.data.length === 0) return null;

      const page = index + 1;
      return buildContestListKey(stableParams, page);
    },
    [enabled, stableParams]
  );

  const fetcher = useCallback(
    async (key: string): Promise<ContestResponse> => {
      const match = key.match(/(?:\?|&)page=(\d+)/);
      const page = match ? Number(match[1]) : 1;

      return ContestAPI.search({
        ...stableParams,
        page,
      });
    },
    [stableParams]
  );

  const { data, error, size, setSize, isValidating, isLoading, mutate } =
    useSWRInfinite<ContestResponse>(getKey, fetcher, {
      initialSize: 1,
      revalidateOnFocus: false,
      revalidateIfStale: false,
      revalidateOnReconnect: false,
      dedupingInterval: 300,
    });

  useEffect(() => {
    if (!enabled) return;
    setSize(1);
  }, [enabled, paramsKey, setSize]);

  const lastPage = data ? data[data.length - 1]?.lastPage ?? 1 : 1;
  const currentPage = data ? data[data.length - 1]?.currentPage ?? 1 : 1;
  const hasMore = currentPage < lastPage;

  const items: ContestResponseData[] = data ? data.flatMap((p) => p.data) : [];

  const loadMore = () => {
    if (!isValidating && hasMore) {
      setSize(size + 1);
    }
  };

  return {
    items,
    isLoading,
    isValidating,
    isError: !!error,
    loadMore,
    hasMore,
    mutate: () => mutate(),
  };
};